/**
 * Utility functions for error handling
 * Follows code review guidelines: avoid type assertions, use type guards
 */

/**
 * Type guard to check if error has a message property
 */
const isErrorWithMessage = (error: unknown): error is { message: string } => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  );
};

/**
 * Type guard for GraphQL errors
 */
export const isGraphQLError = (
  error: unknown,
): error is {
  response?: {
    errors?: Array<{
      message?: string;
      extensions?: { code?: string };
      path?: string[];
    }>;
  };
} => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response: unknown }).response === 'object'
  );
};

/**
 * Safely extracts error message from unknown error type
 * Avoids type assertions as per code review guidelines
 */
export const getErrorMessage = (error: unknown): string => {
  if (isGraphQLError(error)) {
    const graphQlMessage = error.response?.errors?.find(
      item => typeof item.message === 'string' && item.message.length > 0,
    )?.message;
    if (graphQlMessage) {
      return graphQlMessage;
    }
  }

  if (isErrorWithMessage(error)) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Unknown error';
};
